// Must stay the first import: it takes a Slack code out of the URL before the
// router, the login redirect or browser telemetry can copy the page address.
import "./strip-slack-return";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import "./index.css";
import { startAnalyticsConsentSync } from "@/lib/otel-consent";
import { createQueryClient } from "@/lib/query-client";
import { router } from "./router";
import { RouterProvider } from "react-router-dom";
import { ThemeProvider } from "./components/theme-provider";
import { AuthProvider } from "./hooks/useAuth";
import { ConsentBanner } from "./components/shared/ConsentBanner";

// `traceparent` is propagated on cross-origin fetches whose URL matches one
// of these regexes (same-origin fetches are propagated unconditionally by
// the browser SDK). Locally we hit tenant-service directly on :3000; in prod
// the API lives on a separate subdomain (api.kopai.app), so we derive the
// match from VITE_API_URL at build time.
function propagationUrls(): RegExp[] {
  const urls: RegExp[] = [/^http:\/\/localhost:3000\//];
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    try {
      const { origin, protocol } = new URL(apiUrl);
      if (protocol === "http:" || protocol === "https:") {
        const escaped = origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        urls.push(new RegExp(`^${escaped}/`));
      }
    } catch {
      // Relative URL (e.g. "/api") — same-origin, auto-propagated.
    }
  }
  return urls;
}

// The browser OTel SDK starts only once the user accepts analytics, and
// stops when they withdraw (ADR-051 §5). Without consent `lib/otel.ts` is
// never even loaded.
startAnalyticsConsentSync({ propagateTraceHeaderCorsUrls: propagationUrls() });

const queryClient = createQueryClient();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="dark" storageKey="kopai-ui-theme">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <RouterProvider router={router} />
            <ConsentBanner />
            <Toaster position="top-right" />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
